export const WORKSPACE_ROLES = ['owner', 'admin', 'agent', 'viewer'] as const;
export type WorkspaceRole = (typeof WORKSPACE_ROLES)[number];

const ROLE_RANK: Record<WorkspaceRole, number> = { viewer: 0, agent: 1, admin: 2, owner: 3 };

export function roleAtLeast(role: WorkspaceRole, minimum: WorkspaceRole): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}

/**
 * Permission matrix. The API enforces it on every endpoint; the web app reads
 * the same table only to hide actions a member cannot take.
 */
export const PERMISSIONS = {
  'workspace.read': 'viewer',
  'workspace.update': 'admin',
  'workspace.delete': 'owner',
  'members.read': 'viewer',
  'members.manage': 'admin',
  'channels.read': 'viewer',
  'channels.manage': 'admin',
  /** Connect personal accounts (own Instagram, Facebook Page, email) whose leads go to the member. */
  'channels.connect_own': 'agent',
  'inbox.read': 'viewer',
  'inbox.respond': 'agent',
  'inbox.manage': 'agent',
  'customers.read': 'viewer',
  'customers.merge': 'agent',
  'customers.delete': 'admin',
  'customers.export': 'admin',
  'leads.create': 'agent',
  'leads.import': 'admin',
  'automations.read': 'viewer',
  'automations.manage': 'admin',
  'activity.read': 'viewer',
  'audit.read': 'admin',
  'sensitive.read': 'agent',
} as const satisfies Record<string, WorkspaceRole>;

export type Permission = keyof typeof PERMISSIONS;

export function can(role: WorkspaceRole | undefined | null, permission: Permission): boolean {
  if (!role) return false;
  return roleAtLeast(role, PERMISSIONS[permission]);
}

export const ROLE_LABELS: Record<WorkspaceRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  agent: 'Agent',
  viewer: 'Viewer',
};

export const ROLE_DESCRIPTIONS: Record<WorkspaceRole, string> = {
  owner: 'Full control, including deleting the workspace.',
  admin: 'Manage team, channels, automations, imports and customer data requests.',
  agent: 'Work their inquiries: reply, assign, add notes, merge customer records and connect their own accounts.',
  viewer: 'Read-only access. Sensitive form fields are hidden.',
};
